// src/lib/gt-shim.js
//
// Drop-in replacement for the gt-react API surface, implemented over
// react-i18next + the local dictionaries in src/translations/.
//
// Why a shim:
//   The codebase has ~371 `<T>` instances + 9 files importing from
//   gt-react. Migrating each call site would be mechanical busywork.
//   Re-implementing the same exports under our own roof lets every
//   call site work unchanged once the import path is swapped — and
//   the runtime now uses local JSON instead of the unfunded
//   cdn.gtx.dev cloud quota that was silently failing.
//
// API parity:
//   - <T>...</T>                — translate text content via i18next
//   - <Var>{value}</Var>        — passthrough (was used for variable
//                                 interpolation in gt-react; we render
//                                 the value inline without translation)
//   - useGT()                   — () => (key) => string
//   - useDict()                 — () => (key) => string  (same as useGT)
//   - useLocale()               — () => string
//   - useLocaleSelector()       — () => { locale, locales, setLocale,
//                                          getLocaleProperties }
//
// Translation lookup rule:
//   - If <T> children is plain text (strings + whitespace), join + look
//     up in i18next. Render the translation.
//   - If <T> children contains JSX (e.g. a <Var>), render children
//     as-is without translation. Most of these are dynamic content
//     (animated placeholder, count-driven text) where the source text
//     wouldn't be a stable dictionary key anyway.
//
// Importers continue to write `from './lib/gt-shim'` (or wherever the
// import resolves). For convenience the existing `import { ... } from
// 'gt-react'` lines are rewritten to point here.

import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { SUPPORTED_LOCALES } from '../i18n';

// ---- T component ----------------------------------------------------

/**
 * Extract a plain text string from a React children tree, or return
 * null if the children include any non-text element (which means we
 * can't safely use the concatenation as a dictionary key).
 */
function plainText(children) {
  const items = React.Children.toArray(children);
  let out = '';
  for (const c of items) {
    if (typeof c === 'string') {
      out += c;
    } else if (typeof c === 'number') {
      out += String(c);
    } else if (c === null || c === undefined || c === false) {
      // ignore
    } else {
      return null; // non-text element present
    }
  }
  // Collapse run of whitespace (gt-react does the same — JSX line
  // breaks turn into space-padded strings that mess up dictionary lookup).
  return out.replace(/\s+/g, ' ').trim();
}

export const T = ({ children, id }) => {
  const { t } = useTranslation();
  const text = plainText(children);
  if (text == null) {
    // Mixed children (e.g. contains <Var>) — render as-is. The Var
    // case is currently dynamic content where translation by source
    // text isn't meaningful anyway.
    return <>{children}</>;
  }
  // Prefer id-based lookup when provided AND it exists in the dict;
  // otherwise fall back to source-text lookup. The dictionaries we
  // ship are mostly keyed by source text, so the fallback is the
  // dominant path. Either way, react-i18next's t() falls back to the
  // key when the entry is missing, so the page still reads in English.
  const key = id || text;
  const translated = t(key, { defaultValue: text });
  return <>{translated}</>;
};

// ---- Var component --------------------------------------------------

// Passthrough — value interpolation point in the source. Translation
// of <Var> content is intentionally a no-op; values are typically
// dynamic (counts, names, animated placeholders) where translation
// would either be a no-op or wrong.
export const Var = ({ children }) => <>{children}</>;

// ---- Hooks ----------------------------------------------------------

export function useGT() {
  const { t } = useTranslation();
  // gt-react's useGT returns a function (key) => translated. The
  // missing-key fallback returns the key itself, matching gt-react's
  // behavior of "show the key if you forgot to add a translation".
  return (key, opts) => t(key, { defaultValue: key, ...opts });
}

// useDict is gt-react's id-based lookup hook. Same implementation
// under the hood — i18next's t() handles both shapes.
export function useDict() {
  return useGT();
}

export function useLocale() {
  const { i18n: i18nInstance } = useTranslation();
  return i18nInstance.language || 'en';
}

// Locale metadata. gt-react returned { nativeName, name, code }. We
// supply a small lookup so callers (LanguagePicker / LanguageBanner)
// keep working. Code-only locales we don't have native names for
// will fall back to the code itself.
const LOCALE_META = {
  en: { code: 'en', name: 'English', nativeName: 'English' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français' },
  zh: { code: 'zh', name: 'Chinese', nativeName: '中文' },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch' },
};

export function useLocaleSelector() {
  const { i18n: i18nInstance } = useTranslation();
  const locale = i18nInstance.language || 'en';

  const setLocale = (code) => {
    if (!code) return;
    i18nInstance.changeLanguage(code);
    // i18n.js's languageChanged listener writes localStorage; nothing
    // else to do here.
  };

  const getLocaleProperties = (code) =>
    LOCALE_META[code] || { code, name: code, nativeName: code };

  return {
    locale,
    locales: SUPPORTED_LOCALES,
    setLocale,
    getLocaleProperties,
  };
}

// GTProvider was the gt-react context root. We don't need a runtime
// provider — i18next is initialized in src/main.jsx via the side-effect
// `import './i18n'` — but we still export a passthrough component so
// any leftover `<GTProvider>` markup compiles without changes.
export const GTProvider = ({ children }) => <>{children}</>;
